// Editor for the landing-page value-prop cards stored in portal_settings. Each
// prop has a stable key, an icon (chosen from the curated value-prop icon set),
// a title, and a body. Admins can add, remove, reorder, and edit cards. The
// parent owns the array; this component reports the full next array on change.
import {
  Box, Paper, TextField, IconButton, Button, MenuItem, Typography, Tooltip, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { VALUE_PROP_ICON_KEYS, resolveValuePropIcon } from '../../../config/icon-registry.js';

const slugifyKey = (title) => title.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '') || 'prop';

const IconPreview = ({ iconKey }) => {
  const Icon = resolveValuePropIcon(iconKey);
  return <Icon fontSize="small" />;
};

const ValuePropsEditor = ({ items, onChange }) => {
  const update = (index, patch) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const remove = (index) => onChange(items.filter((_, i) => i !== index));

  const move = (index, delta) => {
    const next = [...items];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const add = () => {
    // Give new cards a unique key so the server's uniqueness check passes.
    const existing = new Set(items.map((i) => i.key));
    let key = 'new_prop';
    let n = 1;
    while (existing.has(key)) { n += 1; key = `new_prop_${n}`; }
    onChange([
      ...items,
      {
        key, icon: VALUE_PROP_ICON_KEYS[0], title: '', body: '',
      },
    ]);
  };

  return (
    <Box>
      <Stack spacing={2}>
        {items.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Paper key={index} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, mb: 2,
            }}
            >
              <IconPreview iconKey={item.icon} />
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                {`Card ${index + 1}`}
              </Typography>
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move card ${index + 1} up`}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton size="small" onClick={() => move(index, 1)} disabled={index === items.length - 1} aria-label={`Move card ${index + 1} down`}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove card">
                <IconButton size="small" color="error" onClick={() => remove(index)} aria-label={`Remove card ${index + 1}`}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  select
                  label="Icon"
                  size="small"
                  value={item.icon}
                  onChange={(e) => update(index, { icon: e.target.value })}
                  sx={{ minWidth: 160 }}
                >
                  {VALUE_PROP_ICON_KEYS.map((key) => {
                    const Icon = resolveValuePropIcon(key);
                    return (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Icon fontSize="small" />
                          {key}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </TextField>
                <TextField
                  label="Title"
                  size="small"
                  fullWidth
                  value={item.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    // Auto-derive a key from the title only while it's still a
                    // generated placeholder, so curated keys are never clobbered.
                    const patch = { title };
                    if (/^new_prop(_\d+)?$/.test(item.key)) patch.key = slugifyKey(title);
                    update(index, patch);
                  }}
                />
              </Box>
              <TextField
                label="Body"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={item.body}
                onChange={(e) => update(index, { body: e.target.value })}
              />
            </Stack>
          </Paper>
        ))}
      </Stack>
      <Button startIcon={<AddIcon />} onClick={add} sx={{ mt: 2 }}>
        Add card
      </Button>
    </Box>
  );
};

export default ValuePropsEditor;
